import { atom, selector, RecoilState, RecoilValueReadOnly } from 'recoil';

export interface ComponentVisibility {
  chat: boolean;
  graph: boolean;
  terminal: boolean;
}

// Load initial state from localStorage
const getInitialVisibility = (): ComponentVisibility => {
  try {
    const saved = localStorage.getItem('componentVisibility');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure at least one component is always visible
      if (parsed.chat || parsed.graph || parsed.terminal) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Failed to load component visibility from localStorage:', error);
  }
  
  // Default: all components visible
  return {
    chat: true,
    graph: true,
    terminal: true
  };
};

export const componentVisibilityState: RecoilState<ComponentVisibility> = atom<ComponentVisibility>({
  key: 'ComponentVisibility',
  default: getInitialVisibility(),
  effects: [
    ({ onSet }) => {
      let timeoutId: NodeJS.Timeout | null = null;
      onSet((newValue, oldValue) => {
        if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
          return;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          try {
            localStorage.setItem('componentVisibility', JSON.stringify(newValue));
          } catch (error) {
            console.warn('Failed to save component visibility to localStorage:', error);
          }
          timeoutId = null;
        }, 50); // 50ms debounce for visibility changes
      });
    }
  ]
});

export type MaximizedComponent = 'chat' | 'graph' | 'terminal' | null;

export const maximizedComponentState: RecoilState<MaximizedComponent> = atom<MaximizedComponent>({
  key: 'MaximizedComponent',
  default: null
});

// Selector to get visible components count
export const visibleComponentsCountSelector: RecoilValueReadOnly<number> = selector<number>({
  key: 'VisibleComponentsCount',
  get: ({ get }) => {
    const state = get(componentVisibilityState);
    return [state.chat, state.graph, state.terminal].filter(Boolean).length;
  }
});

// Selector to get visible components array
export const visibleComponentsSelector: RecoilValueReadOnly<string[]> = selector<string[]>({
  key: 'VisibleComponents',
  get: ({ get }) => {
    const state = get(componentVisibilityState);
    const components: string[] = [];
    if (state.chat) components.push('chat');
    if (state.graph) components.push('graph');
    if (state.terminal) components.push('terminal');
    return components;
  }
});
